import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useAppSelector, useAppDispatch } from "@/services/hooks";
import { Link } from "react-router";
import SimpleHeader from "@/components/Headers/SimpleHeader";
import { addHistoriesPages } from "@/services/historiesPagesServices/historiesPagesAction";
import { getPageById } from "@/services/pageServices/pageAction";
import { getUsers } from "@/services/userServices/userActions";
import { resolveContentDeclarations } from "./updatePageHelpers";
import NotificationAlertOptions from "@/layouts/Alerts";
import { Row, Col } from "reactstrap";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import withRouter from "@/utils/withRouter";
import ContentStringField from "../../variableFields/ContentStringField";
import { ToastContainer } from "react-toastify";
import { asCaught } from "@/services/caughtError";

const asString = (v: unknown): string => (typeof v === "string" ? v : v == null ? "" : String(v));

type PageContentEntry = Record<string, unknown> & { variable?: string; value: string };

/** The page being edited: the `getPageById` answer with its text fields coerced to strings. */
type PageToRender = Record<string, unknown> & {
  global_title: string;
  meta_description: string;
  page_content: PageContentEntry[];
};

type UpdatePageContentProps = {
  router?: { params?: { id?: string } };
};

function UpdatePageContent(props: UpdatePageContentProps) {
  const dispatch = useAppDispatch();

  const users = useAppSelector((s) => s.users?.users);
  const pageById = useAppSelector((s) => s.pages.page_to_update);

  // The page being edited, or null until `foundPage` resolves.
  const [pageToRender, setPageToRender] = useState<PageToRender | null>(null);

  const pageId = props?.router?.params?.id;
  // SCRUM-1809: the body comes from the `getPageById` answer already
  // dispatched below for the template (SCRUM-1514). SCRUM-1812: the site
  // response no longer carries pages, so there is no store fallback — the
  // screen waits for that one fetch.
  const foundPage = useMemo(() => {
    if (String(pageById?._id || "") === String(pageId || "")) return pageById;
    return undefined;
  }, [pageId, pageById]);

  // `authorname` is a String content var that ContentStringField renders as a
  // user picker; load the user list once so the Edit screen offers the same
  // options as the Settings screen (SCRUM-924). Guard on length so an empty
  // response does not re-trigger the fetch forever.
  useEffect(() => {
    if (!users || users.length === 0) {
      dispatch(getUsers());
    }
  }, [dispatch, users?.length]);

  const authornameOptions = useMemo(
    () =>
      (users || [])
        .filter((user) => user.role === "CONTENTEDITOR")
        .map((user) => ({ label: user.name, value: user.name })),
    [users]
  );


  useEffect(() => {
    if (!foundPage) return;

    const safeContent = Array.isArray(foundPage.page_content)
      ? foundPage.page_content.map((it) => ({
          ...it,
          value: asString(it?.value),
        }))
      : [];

    setPageToRender({
      ...foundPage,
      global_title: asString(foundPage.global_title),
      meta_description: asString(foundPage.meta_description),
      page_content: safeContent,
    });
  }, [foundPage?._id]);

  // SCRUM-1514 — this screen reads its page out of the SITE list, and
  // `controllers/site.js` populates `page_template` with `'name type'` only, so
  // the declaration it used to switch on (`blk.type` for which editor to render,
  // `blk.variabletype` for the Required/Optional badge) came entirely from the
  // page's stored copy. That copy is what the SCRUM-1511 migration deletes and
  // what this ticket stops writing, so without a template this screen would show
  // no content blocks at all on a page created after either change — silently,
  // since the filter simply matches nothing.
  //
  // `getPageById` populates the template minus `settings_variables`
  // (`controllers/page.js:1228-1235`), i.e. including `content_variables`, and is
  // the same route UpdatePageContainer already takes. Guarded on the id so a
  // stale `page_to_update` from another page is not used.
  useEffect(() => {
    if (pageId) dispatch(getPageById(pageId));
  }, [dispatch, pageId]);

  const template =
    String(pageById?._id || "") === String(pageId || "")
      ? pageById?.page_template
      : undefined;

  // Render list only. `pageToRender` stays the save state and keeps whatever
  // shape the page is stored in, so opening this screen does not migrate a page
  // one way or the other.
  const renderContent = useMemo(
    () => resolveContentDeclarations(pageToRender?.page_content, template),
    [pageToRender?.page_content, template]
  );

  const handleQuillChange = useCallback((newVal, itemVar) => {
    setPageToRender((prev) => {
      if (!prev) return prev;
      const next = {
        ...prev,
        page_content: (prev.page_content || []).map((it) =>
          it.variable === itemVar ? { ...it, value: asString(newVal) } : it
        ),
      };
      return next;
    });
  }, []);

 
  const onSubmitChanges = useCallback(async () => {
    if (!pageToRender) return;

    // Only confirm success when the staging POST actually succeeded; addHistoriesPages
    // now re-throws on failure, so a server-side error is surfaced instead of being
    // masked by an unconditional "Changes queued" toast (SCRUM-755).
    try {
      await dispatch(addHistoriesPages(pageToRender));
      NotificationAlertOptions("success", "Changes queued to history.");
    } catch (err) {
      NotificationAlertOptions(
        "error",
        asCaught(err)?.response?.data?.message || "Saving changes failed. Please try again."
      );
    }
  }, [dispatch, pageToRender]);

  if (!pageToRender) {
    return (
      <>
        <div className="rna-wrapper">
          <ToastContainer />
        </div>
        <SimpleHeader name="Update page content" parentName="Pages" />
        <Row>
          <Col>
            <div className="p-4">Loading page…</div>
          </Col>
        </Row>
      </>
    );
  }

  return (
    <>
      <div className="rna-wrapper">
        <ToastContainer />
      </div>

      <SimpleHeader name="Update page content" parentName="Pages" />

      <Row>
        <Col>
          <center>
            <h2 className="mb-0">
              Updating{" "}
              <span className="text-primary">
                {asString(pageToRender.global_title)}
              </span>{" "}
              content
            </h2>
          </center>
        </Col>
      </Row>

      <Row>
        <Col sm="12" md={{ size: 10, offset: 1 }}>
          {renderContent
            .filter((blk) => blk?.type === "String" || blk?.type === "html")
            .map((blk, idx) => (
              <div key={`${blk.variable}_${idx}`} className="mb-4">
                <h3 className="mb-2">
                  {blk.variable}:{" "}
                  {blk.variabletype === "ContentRequired" ? (
                    <span className="form-control-label text-danger">Required</span>
                  ) : (
                    <span className="form-control-label text-primary">Optional</span>
                  )}
                </h3>

                {blk.type === "html" ? (
                  <ReactQuill
                    id={`${blk.variable}_${idx}`}
                    theme="snow"
                    value={asString(blk.value)}
                    onChange={(val) => handleQuillChange(val, blk.variable)}
                  />
                ) : (
                  // Plain String vars render through the registry field (textarea,
                  // plus the authorname/date special-cases) exactly like the
                  // Settings screen — no longer Quill, which HTML-corrupted plain
                  // text and dropped the authorname/date pickers (SCRUM-924).
                  <ContentStringField
                    settingVar={blk}
                    options={authornameOptions}
                    onChange={(name, value) => handleQuillChange(value, name)}
                  />
                )}
              </div>
            ))}
        </Col>
      </Row>

      <br />

      <Row>
        <Col>
          <Link
            to="/admin/display-pages"
            className="btn btn-outline-default mr-1 float-left"
          >
            Back To Pages
          </Link>

          <button
            className="btn btn-outline-primary mr-1 float-right"
            type="button"
            onClick={onSubmitChanges}
          >
            Finish Updating
          </button>
        </Col>
      </Row>
      <br />
    </>
  );
}

export default withRouter(UpdatePageContent);
